import { useEffect, useMemo, useState } from "react";
import api from "../../../../api/axiosConfig";

/* =====================================================
   MAPEO DE ADICIONALES SEGÚN REGLA DE NEGOCIO
===================================================== */
const MAPEO_ADICIONALES = {
  JUGOS_NATURALES: ["ADICIONAL FRUTA"],

  SNACKS_Y_ENSALADAS: ["ADICIONAL TOPPINGS"],

  COMIDAS: ["ADICIONAL COMIDA"],
};

/* =====================================================
   NORMALIZAR SEGURO
===================================================== */
const normalizar = (v) =>
  String(v ?? "")
    .toUpperCase()
    .trim();

/* =====================================================
   HOOK
===================================================== */
export function useExtras({ producto, cantidad, personalizarCadaUno }) {
  const [extrasBD, setExtrasBD] = useState([]);
  const [extrasQty, setExtrasQty] = useState({});
  const [configPorUnidad, setConfigPorUnidad] = useState([]);
  const [loadingExtras, setLoadingExtras] = useState(true);

  /* =====================================================
     DETERMINAR CATEGORÍA DEL PRODUCTO
  ===================================================== */
  const categoriaProducto = normalizar(
    producto?.categoria?.nombre || producto?.categoria
  );

  let tipoProducto = "COMIDAS";

  if (categoriaProducto.includes("JUGO")) {
    tipoProducto = "JUGOS_NATURALES";
  } else if (
    categoriaProducto.includes("SNAK") || // por "Snaks"
    categoriaProducto.includes("SNACK") ||
    categoriaProducto.includes("ENSALADA")
  ) {
    tipoProducto = "SNACKS_Y_ENSALADAS";
  }

  const esJugo = tipoProducto === "JUGOS_NATURALES";

  const categoriasExtrasPermitidas =
    MAPEO_ADICIONALES[tipoProducto] || [];

  /* =====================================================
     CARGAR ADICIONALES
  ===================================================== */
  useEffect(() => {
    let alive = true;

    async function cargarExtras() {
      if (!producto) return;

      try {
        setLoadingExtras(true);

        const res = await api.get("/public/productos");
        const data = res.data || [];

        const filtrados = data.filter((p) =>
          categoriasExtrasPermitidas.includes(
            normalizar(
              p.categoria?.nombre || p.categoria
            )
          )
        );

        if (alive) setExtrasBD(filtrados);
      } catch (e) {
        console.error("Error cargando adicionales", e);
        if (alive) setExtrasBD([]);
      } finally {
        if (alive) setLoadingExtras(false);
      }
    }

    cargarExtras();
    return () => {
      alive = false;
    };
  }, [producto, categoriasExtrasPermitidas]);

  /* =====================================================
     CONFIGURACIÓN POR UNIDAD
  ===================================================== */
  useEffect(() => {
    if (!personalizarCadaUno) return;

    setConfigPorUnidad((prev) => {
      const next = Array.from({ length: cantidad }, (_, i) => {
        const old = prev[i];
        return (
          old || {
            base: "",
            nota: "",
            extrasQty: {},
          }
        );
      });
      return next;
    });
  }, [cantidad, personalizarCadaUno]);

  /* =====================================================
     HANDLERS EXTRAS (NORMAL)
  ===================================================== */
  const incExtra = (id) => {
    setExtrasQty((prev) => ({
      ...prev,
      [id]: (prev[id] || 0) + 1,
    }));
  };

  const decExtra = (id) => {
    setExtrasQty((prev) => {
      const next = { ...prev };
      const v = (next[id] || 0) - 1;
      if (v <= 0) delete next[id];
      else next[id] = v;
      return next;
    });
  };

  /* =====================================================
     HANDLERS EXTRAS (POR UNIDAD)
  ===================================================== */
  const incExtraUnidad = (idx, idExtra) => {
    setConfigPorUnidad((prev) =>
      prev.map((u, i) => {
        if (i !== idx) return u;
        const nextExtras = { ...(u.extrasQty || {}) };
        nextExtras[idExtra] =
          (nextExtras[idExtra] || 0) + 1;
        return { ...u, extrasQty: nextExtras };
      })
    );
  };

  const decExtraUnidad = (idx, idExtra) => {
    setConfigPorUnidad((prev) =>
      prev.map((u, i) => {
        if (i !== idx) return u;
        const nextExtras = { ...(u.extrasQty || {}) };
        const v = (nextExtras[idExtra] || 0) - 1;
        if (v <= 0) delete nextExtras[idExtra];
        else nextExtras[idExtra] = v;
        return { ...u, extrasQty: nextExtras };
      })
    );
  };

  /* =====================================================
     EXTRAS SELECCIONADOS
  ===================================================== */
  const extrasSeleccionados = useMemo(() => {
    return extrasBD
      .map((e) => ({
        id: e.id,
        nombre: e.nombre,
        precio: Number(e.precio || 0),
        qty: extrasQty[e.id] || 0,
      }))
      .filter((e) => e.qty > 0);
  }, [extrasBD, extrasQty]);

  /* =====================================================
     TOTALES
  ===================================================== */
  const extrasTotal = useMemo(() => {
    return extrasSeleccionados.reduce(
      (sum, e) => sum + e.precio * e.qty,
      0
    );
  }, [extrasSeleccionados]);

  const extrasTotalVisual = useMemo(() => {
    if (!personalizarCadaUno) return extrasTotal;

    return configPorUnidad.reduce((sumUnidad, u) => {
      const extrasUnidadTotal = extrasBD.reduce(
        (sum, e) => {
          const qty =
            (u.extrasQty || {})[e.id] || 0;
          return sum + Number(e.precio || 0) * qty;
        },
        0
      );

      return sumUnidad + extrasUnidadTotal;
    }, 0);
  }, [personalizarCadaUno, extrasTotal, configPorUnidad, extrasBD]);

  /* =====================================================
     RETURN
  ===================================================== */
  return {
    // flags
    esJugo,
    tipoProducto,
    loadingExtras,


    extrasBD,
    extrasQty,
    configPorUnidad,

    setExtrasQty,
    setConfigPorUnidad,


    incExtra,
    decExtra,
    incExtraUnidad,
    decExtraUnidad,

    extrasSeleccionados,
    extrasTotal,
    extrasTotalVisual,
  };
}
